import { Modal, message } from 'antd'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyBtn } from '~/components/ui/MyBtn'
import {
  AI_CREATE_ARTWORK,
  GET_AI_TOTAL_INFO,
  AI_PRE_CREATE,
  GET_AI_MEMBER_LIST,
} from '~/graphqls/gqls'
import {
  aiCreateArtwork,
  aiCreateArtworkVariables,
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu,
  getAITotalInfo,
  getAIMemberListVariables,
  getAIMemberList_accountModule_userMemberCardList,
  getAIMemberList,
  aiPreCreate,
  aiPreCreateVariables,
  AiCreateArtworkModel,
  AiCreateArtworkPattern,
  GPTVisibleState,
} from '~/graphqls/types'
import { useMutation } from '@apollo/client'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { graphQLErrorMessage, trackEvent } from '~/utils'
import QuickPreview from '~/components/pages/ai/QuickPreview'
import { MyIcon } from '~/components/ui/MyIcon'
import NoLoginQuickPreview from '~/components/pages/ai/NoLoginQuickPreview'
import { observer } from 'mobx-react-lite'
import { useStores } from '~/hooks/useStores'
import PreCreateDetail from '~/components/pages/ai/PreCreateDetail'
import { AiHeader } from '~/components/pages/ai/ai-homepage/AiHeader'
import { AiForm, AiFormHandles } from '~/components/pages/ai/common/AiForm'
import { useAuth } from '~/hooks/useAuth'
import { LabHeader } from './LabHeader'
import { pattern2Header } from '~/configs/ai'
import { useValidateIntergal } from '~/hooks/useValidateIntergal'

const Framework = ({
  options,
  totalInfo: _totalInfo,
  memberList: _memberList,
  lockModel,
  lockModelCode,
  lockPattern = AiCreateArtworkPattern.GENERAL,
}: {
  options: getAIMenu_pictureGraph_aiCreateArtworkOptionMenu
  totalInfo: getAITotalInfo_pictureGraph_aiArtworkTotalInfo
  memberList: getAIMemberList_accountModule_userMemberCardList[]
  /** 锁定model版本 */
  lockModel?: AiCreateArtworkModel
  /** 锁定modelCode */
  lockModelCode?: number
  /** 锁定pattern */
  lockPattern?: AiCreateArtworkPattern
}) => {
  const { store } = useStores()
  const { isLogin } = useAuth()
  const { IsNotEnough, intergalModal } = useValidateIntergal()

  const [totalInfo, setTotalInfo] = useState(_totalInfo)
  const [memberList, setMemberList] = useState(_memberList)

  const formRef = useRef<AiFormHandles>(null)

  const [loading, setLoading] = useState(false)
  const { validateIsLogin } = useValidateAuth()
  const [gptVisibleState, setGptVisibleState] = useState(GPTVisibleState.idle)

  const getTotalInfoAction =
    useImperativeQuery<getAITotalInfo>(GET_AI_TOTAL_INFO)

  const getMemberListAction = useImperativeQuery<
    getAIMemberList,
    getAIMemberListVariables
  >(GET_AI_MEMBER_LIST)

  const [aiCreateArtworkAction] = useMutation<
    aiCreateArtwork,
    aiCreateArtworkVariables
  >(AI_CREATE_ARTWORK)

  const aiPreCreateAction = useImperativeQuery<
    aiPreCreate,
    aiPreCreateVariables
  >(AI_PRE_CREATE)

  // 消耗明细 modal
  const detailModalLoading = useRef(false)
  const detailModal = async () => {
    if (detailModalLoading.current) {
      return
    }
    if (!validateIsLogin()) {
      return
    }

    const formParams = await formRef.current?.getParams()

    try {
      detailModalLoading.current = true

      const preRes = await aiPreCreateAction({
        param: {
          accelerateTimes: formParams?.accelarateTimes,
          prompt: formParams?.prompt as string,
          size: formParams?.size,
          width: formParams?.width as number,
          length: formParams?.length as number,
          model: formParams?.model,
          initImageUrl: formParams?.initImageUrl,
          num: formParams?.num,
          superType: formParams?.superType,
          modelCode: formParams?.modelCode,
          pattern: formParams?.pattern,
        },
      })

      return Modal.info({
        title: '作画明细',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <PreCreateDetail
            detail={preRes.data.pictureGraph?.aiArtworkPayStrategyTips!}
          />
        ),
        okText: '知道了',
        onOk: close => {
          close()
        },
      })
    } catch (err) {
      message.destroy()
      message.error(graphQLErrorMessage(err) || '获取明细失败，请刷新')
    } finally {
      detailModalLoading.current = false
    }
  }

  /** 立即生成 */
  const submitLock = useRef(false)
  const handleSubmit = async () => {
    if (submitLock.current) {
      return
    }
    if (!validateIsLogin()) {
      return
    }

    const formParams = await formRef.current?.getParams()

    try {
      submitLock.current = true
      setLoading(true)
      const preRes = await aiPreCreateAction({
        param: {
          accelerateTimes: formParams?.accelarateTimes,
          prompt: formParams?.prompt as string,
          size: formParams?.size,
          width: formParams?.width as number,
          length: formParams?.length as number,
          model: formParams?.model,
          initImageUrl: formParams?.initImageUrl,
          num: formParams?.num,
          superType: formParams?.superType,
          modelCode: formParams?.modelCode,
          pattern: formParams?.pattern,
          chatGptOptimize: formParams?.isGPT,
          maskImageUrl: formParams?.maskImageUrl,
          maskZoneImageUrl: formParams?.maskZoneImageUrl,
        },
      })
      await aiCreateArtworkAction({
        variables: {
          param: {
            chatGptOptimize: formParams?.isGPT,
            pattern: formParams?.pattern,
            modelCode: formParams?.modelCode,
            maskImageUrl: formParams?.maskImageUrl,
            maskZoneImageUrl: formParams?.maskZoneImageUrl,
            accelerateTimes: formParams?.accelarateTimes,
            styleDecorationOptions: formParams?.styleDecorationOptions,
            character: formParams?.character,
            modelFusion: formParams?.modelFusion,
            prompt: formParams?.prompt as string,
            size: formParams?.size,
            width: formParams?.width as number,
            length: formParams?.length as number,
            superType: formParams?.superType,
            model: formParams?.model,
            initImageUrl: formParams?.initImageUrl,
            num: formParams?.num,
            creativityDegree: formParams?.creativityDegree,
            promptOptimize: formParams?.promptOptimize,
            ucPrompt: formParams?.ucPrompt || undefined,
            repairTheHand: formParams?.doRepairTheHand || false,
            initWidth: formParams?.initWidth,
            initHeight: formParams?.initHeight,
            seed: formParams?.seed || null,
            cfg: formParams?.cfg || null,
            ensd: formParams?.ensd || null,
            clipSkip: formParams?.clipSkip || null,
            pretreatmentMethod: formParams?.pretreatmentMethod || null,
            samplerIndex: formParams?.samplerIndex,
            // steps: formParams?.steps,
            payStrategy: {
              integralUse: {
                integralUseOnAccelerate:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnAccelerate,
                integralUseOnCreate:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnCreate,
                integralUseOnResolution:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnResolution,
              },
              userRightsUse: {
                dailyFreeCreateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .userRightsUse.dailyFreeCreateTimesUse,
                deservedCreateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .userRightsUse.deservedCreateTimesUse,
              },
              vipRightsUse: {
                accelerateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .vipRightsUse.accelerateTimesUse,
                createTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .vipRightsUse.createTimesUse,
              },
            },
          },
        },
      })

      if (!!formParams?.trackEventLabel) {
        try {
          const trackParam = JSON.parse(formParams?.trackEventLabel)
          trackEvent(trackParam)
        } catch (error) {}
      }

      // 重置一些状态
      formRef.current?.reset()

      store.ai?.setIsRefreshMemberList(true)
      store.ai?.setIsRefreshTotalInfo(true)
      store.ai?.setIsRefeshPreviewData(true)
      store.ai?.setIsResetSelectProduct(true)

      window.scroll({
        top: lockPattern === AiCreateArtworkPattern.GENERAL ? 0 : 190,
      })
    } catch (err) {
      /** 积分不足 */
      if (IsNotEnough(err)) {
        intergalModal()
      } else {
        message.destroy()
        message.error(graphQLErrorMessage(err) || '生成失败')
      }
    } finally {
      setLoading(false)
      submitLock.current = false
    }
  }

  useEffect(() => {
    /** 作画时间等 */
    if (store.ai?.isRefreshTotalInfo) {
      getTotalInfoAction()
        .then(res => {
          setTotalInfo(
            (res.data.pictureGraph
              ?.aiArtworkTotalInfo as getAITotalInfo_pictureGraph_aiArtworkTotalInfo) ||
              {}
          )
        })
        .catch(err => {
          message.destroy()
          message.error('更新AI作画信息失败，请刷新')
        })
        .finally(() => {
          store.ai?.setIsRefreshTotalInfo(false)
        })
    }
  }, [store.ai?.isRefreshTotalInfo])

  useEffect(() => {
    /** 权益卡 */
    if (store.ai?.isRefreshMemberList) {
      getMemberListAction()
        .then(res => {
          setMemberList(res.data.accountModule?.userMemberCardList || [])
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          store.ai?.setIsRefreshMemberList(false)
        })
    }
  }, [store.ai?.isRefreshMemberList])

  const labHeader =
    lockPattern !== AiCreateArtworkPattern.GENERAL &&
    pattern2Header[lockPattern as keyof typeof pattern2Header]

  return (
    <>
      {labHeader && (
        <div className="-mt-6">
          <LabHeader
            memberList={memberList}
            totalInfo={totalInfo}
            header={labHeader}
          />
        </div>
      )}

      <MyContainer
        className={classNames({ 'bg-neutral-100 !pt-3 !pb-4': !!labHeader })}
      >
        <div className="flex justify-between">
          <div className="flex-1 relative w-[738px]">
            {lockPattern === AiCreateArtworkPattern.GENERAL && (
              <AiHeader totalInfo={totalInfo} memberList={memberList} />
            )}
            <AiForm
              options={options}
              totalInfo={totalInfo}
              memberList={memberList}
              ref={formRef}
              lockModel={lockModel}
              lockModelCode={lockModelCode}
              lockPattern={lockPattern}
              gptVisibleState={gptVisibleState}
              setGptVisibleState={setGptVisibleState}
            />

            <div className="sticky pb-8 bottom-0 bg-neutral-100">
              <div className="shadow-[0_-3px_3px_0px_rgba(252,252,253,0.08)] w-full h-1 mb-2"></div>
              <div className="flex items-center gap-3">
                {gptVisibleState == GPTVisibleState.idle ? (
                  <MyBtn
                    className={classNames('flex-1', 'relative')}
                    type="primary"
                    size="large"
                    htmlType="submit"
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    {!!store.ai?.comsumedPoints ? (
                      <>
                        <div className="text-base !leading-none font-semibold mt-0.5">
                          立即生成
                        </div>
                        <div
                          className="text-xs"
                          style={{
                            transform: 'scale(0.833)',
                          }}
                        >
                          （消耗{store.ai?.comsumedPoints}积分）
                        </div>
                      </>
                    ) : (
                      '立即生成'
                    )}

                    <div
                      className="rounded-full w-8 h-8 absolute right-2 top-1.5 flex items-center justify-center"
                      style={{
                        background: 'rgba(255, 255, 255, 0.16)',
                      }}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        detailModal()
                      }}
                    >
                      <MyIcon
                        icon="file-doc"
                        className="block text-[16px] font-normal"
                      />
                    </div>
                  </MyBtn>
                ) : (
                  <div
                    className="flex-1 items-center justify-center bg-[var(--neutral-200)] !h-[44px] rounded-full"
                    onClick={() => {}}
                  >
                    <div className="flex h-full items-center justify-center">
                      <div className="text-[var(--neutral-400)] text-sm font-semibold">
                        智能描述 生成中
                      </div>
                      <img
                        alt=""
                        src="/images/ai/chatgpt_loading_gray.png"
                        className="h-7"
                      ></img>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={classNames('ml-[22px] w-[632px]', {
              'mt-6': lockPattern !== AiCreateArtworkPattern.GENERAL,
            })}
          >
            {isLogin ? (
              <QuickPreview memberList={memberList} lockPattern={lockPattern} />
            ) : (
              <NoLoginQuickPreview
                data={((options.defaultArtWorkList || []) as string[]).slice(
                  0,
                  28
                )}
              />
            )}
          </div>
        </div>
      </MyContainer>
    </>
  )
}

export default observer(Framework)
